import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { CheckoutIntentResponse } from "@hybrid-escrow/shared";
import { apiClient, newIdempotencyKey } from "../lib/api-client";
import { useListing } from "../hooks/useListings";
import { usePublicConfig } from "../hooks/usePublicConfig";
import { useAuthPrincipal } from "../hooks/useAuthPrincipal";
import { useCheckoutUserOperation } from "../lib/coinbase/useCheckoutUserOperation";
import { cdpNetworkForChainId, chainNameForChainId } from "../lib/coinbase/network";
import { Money } from "../components/Money";
import { ErrorBanner, LoadingState } from "../components/States";
import { formatDurationSeconds } from "../lib/format";

export function CheckoutPage() {
  const { listingId } = useParams<{ listingId: string }>();
  const [searchParams] = useSearchParams();
  // Present only when arriving from an accepted Best Offer (see MyOffersPage) — checks out at
  // that negotiated price instead of the listing's standing price.
  const offerId = searchParams.get("offerId") ?? undefined;
  const navigate = useNavigate();
  const { data: listing } = useListing(listingId);
  const { data: config } = usePublicConfig();
  const { smartAccountAddress } = useAuthPrincipal();

  const idempotencyKeyRef = useRef(newIdempotencyKey());
  const [intent, setIntent] = useState<CheckoutIntentResponse | null>(null);
  const [quoteError, setQuoteError] = useState<unknown>(null);
  const [isRequestingQuote, setIsRequestingQuote] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  // A coupon only ever discounts the listing's own standing price — mutually exclusive with a
  // negotiated Best Offer (enforced server-side too), so the input is hidden entirely on an
  // offer-based checkout.
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<string | undefined>(undefined);

  const op = useCheckoutUserOperation();

  // Takes the coupon explicitly (rather than always reading `appliedCoupon` from state) so
  // reapplying the same code after a failed attempt — e.g. retrying a transient network error —
  // always fires a fresh request instead of silently no-op'ing because the code didn't change.
  const requestQuote = useCallback(
    async (couponCode?: string) => {
      if (!listingId) return;
      setIsRequestingQuote(true);
      setQuoteError(null);
      try {
        const result = await apiClient.createCheckoutIntent({
          listingId,
          offerId,
          couponCode,
          idempotencyKey: idempotencyKeyRef.current,
        });
        setIntent(result);
      } catch (err) {
        setQuoteError(err);
      } finally {
        setIsRequestingQuote(false);
      }
    },
    [listingId, offerId],
  );

  function handleApplyCoupon() {
    const trimmed = couponInput.trim();
    if (!trimmed) return;
    // A fresh idempotency key: this is a genuinely different quote (different discounted
    // principal), not a retry of the same checkout attempt.
    idempotencyKeyRef.current = newIdempotencyKey();
    setIntent(null);
    setAppliedCoupon(trimmed);
    void requestQuote(trimmed);
  }

  function handleRemoveCoupon() {
    idempotencyKeyRef.current = newIdempotencyKey();
    setIntent(null);
    setQuoteError(null);
    setCouponInput("");
    setAppliedCoupon(undefined);
    void requestQuote();
  }

  // Only on mount / when the listing or offer changes — reapplying a coupon is handled
  // explicitly by the handlers above, not by this effect.
  useEffect(() => {
    void requestQuote();
  }, [listingId, offerId]);

  useEffect(() => {
    if (op.phase === "submitted" && intent) {
      // Best-effort, purely informational per the API contract — never treated as proof of funding.
      apiClient
        .submitCheckoutIntent(intent.internalOrderId, {
          userOperationHash: op.userOperationHash ?? undefined,
        })
        .catch(() => {
          setReportError(
            "We couldn't record your submission with the server, but your transaction was sent. " +
              "It will still be picked up automatically once confirmed onchain.",
          );
        });
    }
  }, [op.phase, op.userOperationHash, intent]);

  // A wallet failure with no operation hash means nothing was ever sent, so the order this
  // checkout created can't land onchain. Cancel it now — otherwise it keeps the listing's license
  // key reserved (or the listing archived) — and quote afresh so "Try again" has a live order.
  // A failure *after* a hash exists (dropped/reverted) is left to the chain worker instead.
  // Once per failure: the re-quote below swaps in a new `intent` while the phase is still
  // "failed", which must not cancel the replacement order too.
  const failureHandledRef = useRef(false);
  useEffect(() => {
    if (op.phase !== "failed") {
      failureHandledRef.current = false;
      return;
    }
    if (failureHandledRef.current || op.userOperationHash || !intent) return;
    failureHandledRef.current = true;
    void (async () => {
      try {
        await apiClient.cancelCheckoutIntent(intent.internalOrderId);
      } catch {
        // Best-effort: the abandoned-order sweep expires it within the hour regardless.
      }
      idempotencyKeyRef.current = newIdempotencyKey();
      await requestQuote(appliedCoupon);
    })();
  }, [op.phase, op.userOperationHash, intent, appliedCoupon, requestQuote]);

  useEffect(() => {
    if (op.phase === "confirmed" && intent) {
      navigate(`/orders/${intent.internalOrderId}`, { replace: true });
    }
  }, [op.phase, intent, navigate]);

  if (!listingId) return null;

  const cdpNetwork = intent ? cdpNetworkForChainId(intent.chainId) : null;
  const networkOk = Boolean(config && intent && config.chainId === intent.chainId && cdpNetwork);
  const canPay = Boolean(
    intent && smartAccountAddress && acknowledged && networkOk && op.phase === "idle",
  );
  const isBusy = op.phase === "awaiting-signature" || op.phase === "submitted";

  async function handleConfirmAndPay() {
    if (!intent || !smartAccountAddress || !cdpNetwork) return;
    setReportError(null);
    await op.submit({
      smartAccountAddress,
      network: cdpNetwork,
      calls: [intent.calls.approve, intent.calls.fundEscrow],
    });
  }

  return (
    <div className="stack" style={{ maxWidth: 640 }}>
      <h1>Checkout</h1>
      {listing && <p className="muted">{listing.title}</p>}

      {isRequestingQuote && !intent && <LoadingState label="Preparing your quote…" />}
      {quoteError !== null && (
        <ErrorBanner
          error={quoteError}
          onRetry={() => void requestQuote(appliedCoupon)}
          title="Couldn't prepare checkout"
        />
      )}

      {!offerId && (
        <div className="card stack">
          {appliedCoupon && intent && intent.couponCode ? (
            <div className="row row--between success-pop">
              <span className="row" style={{ gap: "0.5rem" }}>
                <span className="success-check" aria-hidden="true">
                  ✓
                </span>
                <span>
                  Coupon <strong className="mono">{intent.couponCode}</strong> applied
                  {intent.discountBaseUnits !== "0" && (
                    <>
                      {" "}
                      — you save <Money baseUnits={intent.discountBaseUnits} />
                    </>
                  )}
                </span>
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={isRequestingQuote}
                onClick={handleRemoveCoupon}
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="row" style={{ gap: "0.5rem" }}>
              <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                <label htmlFor="coupon-code">Coupon code</label>
                <input
                  id="coupon-code"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value)}
                  placeholder="First-purchase code, if you have one"
                  maxLength={40}
                />
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={!couponInput.trim() || isRequestingQuote}
                onClick={handleApplyCoupon}
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}

      {intent && (
        <>
          <div className="fee-breakdown">
            <dl>
              <div className="row row--between">
                <dt>Seller price (order principal)</dt>
                <dd>
                  <Money baseUnits={intent.principalBaseUnits} />
                </dd>
              </div>
              {intent.discountBaseUnits !== "0" && (
                <div className="row row--between">
                  <dt>Coupon discount</dt>
                  <dd>
                    -<Money baseUnits={intent.discountBaseUnits} />
                  </dd>
                </div>
              )}
              <div className="row row--between">
                <dt>Platform fee ({(intent.platformFeeBps / 100).toFixed(2)}%)</dt>
                <dd>
                  <Money baseUnits={intent.platformFeeBaseUnits} />
                </dd>
              </div>
              <div className="row row--between total">
                <dt>Total due now</dt>
                <dd>
                  <Money baseUnits={intent.totalDueBaseUnits} />
                </dd>
              </div>
            </dl>
          </div>

          <div className="alert alert--warning" role="alert">
            <strong>The platform fee is nonrefundable.</strong>
            <p>{intent.nonrefundableFeeNotice}</p>
          </div>

          <div className="card stack">
            <div className="row row--between">
              <span className="muted">Seller</span>
              <span className="mono">{intent.sellerAddress}</span>
            </div>
            <div className="row row--between">
              <span className="muted">Network</span>
              <span>{chainNameForChainId(intent.chainId)}</span>
            </div>
            <div className="row row--between">
              <span className="muted">Dispute window</span>
              <span>{formatDurationSeconds(intent.disputeDeadlineSeconds)} after funding</span>
            </div>
          </div>

          {!networkOk && (
            <div className="alert alert--danger" role="alert">
              <strong>Wrong network.</strong>
              <p>
                This quote targets chain {intent.chainId}, which this deployment can&apos;t safely
                submit a transaction to. Checkout is disabled — refresh the page or contact support.
              </p>
            </div>
          )}

          {!smartAccountAddress && (
            <div className="alert alert--warning" role="alert">
              Your Smart Account isn&apos;t ready yet. Reopen your wallet panel or refresh the page.
            </div>
          )}

          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={acknowledged}
              disabled={isBusy || op.phase === "confirmed"}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            <span>
              I understand this payment is made in cryptocurrency and is irreversible once
              confirmed. The 10% platform fee is nonrefundable even if this order is later disputed
              and refunded. I have {formatDurationSeconds(intent.disputeDeadlineSeconds)} from
              funding to open a dispute if there&apos;s a problem.
            </span>
          </label>

          {/* A quiet confirmation the payment is genuinely progressing, not stalled — three
              plain dots rather than anything celebratory, since this is money in flight and
              "calm and certain" matters more here than "delightful". */}
          {isBusy && (
            <div className="pay-progress" role="status" aria-label={`Payment step: ${op.phase}`}>
              <span className="pay-progress__dot pay-progress__dot--done" />
              <span
                className={`pay-progress__dot ${
                  op.phase === "awaiting-signature"
                    ? "pay-progress__dot--active"
                    : "pay-progress__dot--done"
                }`}
              />
              <span
                className={`pay-progress__dot ${
                  op.phase === "submitted" ? "pay-progress__dot--active" : ""
                }`}
              />
            </div>
          )}

          <button
            type="button"
            className="btn btn-primary btn-block"
            disabled={!canPay}
            onClick={() => void handleConfirmAndPay()}
          >
            {op.phase === "awaiting-signature" && "Confirm in your wallet…"}
            {op.phase === "submitted" && "Waiting for confirmation…"}
            {(op.phase === "idle" || op.phase === "failed") && (
              <>
                Confirm & pay <Money baseUnits={intent.totalDueBaseUnits} />
              </>
            )}
          </button>

          {op.phase === "submitted" && (
            <p className="text-sm muted" role="status" aria-live="polite">
              Transaction submitted
              {op.userOperationHash ? ` (op ${op.userOperationHash.slice(0, 10)}…)` : ""}. This can
              take a little while — you&apos;ll be redirected once it&apos;s confirmed onchain.
            </p>
          )}

          {op.phase === "failed" && op.error && (
            <div className="alert alert--danger" role="alert">
              <strong>The transaction didn&apos;t go through.</strong>
              <p>{op.error}</p>
              <button type="button" className="btn btn-secondary btn-sm" onClick={op.reset}>
                Try again
              </button>
            </div>
          )}

          {reportError && <p className="text-sm muted">{reportError}</p>}
        </>
      )}
    </div>
  );
}
